import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { COMPANY } from "../../data/site";

const links = [
  { label: "Services", href: "#services" },
  { label: "How it works", href: "#how" },
  { label: "Trust", href: "#trust" },
  { label: "Pricing", href: "#pricing" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#D5D1C8]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 group">
          <span className="w-2.5 h-2.5 rounded-full bg-[#C26D5C] group-hover:scale-125 transition-transform duration-300" />
          <span className="font-serif text-2xl tracking-tight text-[#1A362D]">{COMPANY.name}</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-[#4A5A53] hover:text-[#1A362D] transition-colors duration-300 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 hover:after:w-full after:bg-[#C26D5C] after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-button"
            onClick={() => go("#book")}
            className="rounded-full bg-[#1A362D] text-[#F9F8F6] text-sm font-medium px-6 py-3 hover:bg-[#C26D5C] transition-colors duration-300"
          >
            Book a helping hand
          </button>
        </nav>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-[#1A362D]" onClick={() => setOpen((o) => !o)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-[#F9F8F6] border-b border-[#D5D1C8]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left font-serif text-2xl text-[#1A362D]">
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#book")} className="rounded-full bg-[#1A362D] text-[#F9F8F6] text-sm font-medium px-6 py-3 mt-2">
                Book a helping hand
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
